/**
 * Utility helper functions
 */

/**
 * Format a date relative to now (e.g., "5 min ago", "2 hours ago")
 */
export const formatRelativeTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)} days ago`;
  return date.toLocaleDateString();
};

/**
 * Format a date for display
 */
export const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Truncate text to a maximum length
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Capitalize first letter of a string
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Generate a unique ID
 */
export const generateId = () => {
  return crypto.randomUUID();
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check password strength
 * Returns: 'weak', 'medium', 'strong'
 */
export const checkPasswordStrength = (password) => {
  if (!password || password.length < 6) return 'weak';
  
  let score = 0;
  
  // Length
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  
  // Character types
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  
  if (score <= 2) return 'weak';
  if (score <= 4) return 'medium';
  return 'strong';
};

/**
 * Get material display info
 */
export const getMaterialInfo = (type) => {
  const materials = {
    concrete: {
      icon: '🧱',
      name: 'Concrete',
      description: 'Slabs, foundations, patios, driveways',
      color: 'bg-gray-100',
    },
    drywall: {
      icon: '📏',
      name: 'Drywall',
      description: 'Walls, ceilings, repairs',
      color: 'bg-blue-50',
    },
    paint: {
      icon: '🎨',
      name: 'Paint',
      description: 'Interior, exterior, primer',
      color: 'bg-purple-50',
    },
    flooring: {
      icon: '🏠',
      name: 'Flooring',
      description: 'Tile, hardwood, vinyl, carpet',
      color: 'bg-amber-50',
    },
    lumber: {
      icon: '🪵',
      name: 'Lumber',
      description: 'Framing, decking, trim',
      color: 'bg-orange-50',
    },
    roofing: {
      icon: '🏗️',
      name: 'Roofing',
      description: 'Shingles, underlayment, flashing',
      color: 'bg-red-50',
    },
    general: {
      icon: '🔧',
      name: 'General',
      description: 'Mixed materials and projects',
      color: 'bg-green-50',
    },
  };
  
  return materials[type] || materials.general;
};

/**
 * Format number with commas
 */
export const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US').format(num);
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy:', error);
    return false;
  }
};
